/**
 * Mock Data Types
 *
 * Type definitions for mock data structures used in local development.
 * Includes simulation types for tutorial videos and marketing screenshots.
 */

// =============================================================================
// PnL Presets for Marketing & Demos
// =============================================================================

export type PnLPreset =
  | "marketing_positive" // +$500 to +$2,000 - Good for marketing screenshots
  | "realistic" // -$500 to +$500 - Normal market conditions
  | "recovery_story" // Starts negative, trends positive over time
  | "cautious_day"; // Small positive +$50 to +$200

export type UpdateSpeed = "slow" | "normal" | "fast";

export const UPDATE_SPEED_MS: Record<UpdateSpeed, number> = {
  slow: 3000,
  normal: 1500,
  fast: 500,
};

export const PNL_PRESET_CONFIG: Record<
  PnLPreset,
  {
    upBias: number; // Probability of upward movement (0-1)
    minPnL: number; // Minimum target PnL
    maxPnL: number; // Maximum target PnL
    description: string;
  }
> = {
  marketing_positive: {
    upBias: 0.75,
    minPnL: 500,
    maxPnL: 2000,
    description: "Positive PnL for marketing screenshots (+$500 to +$2,000)",
  },
  realistic: {
    upBias: 0.5,
    minPnL: -500,
    maxPnL: 500,
    description: "Realistic market conditions (-$500 to +$500)",
  },
  recovery_story: {
    upBias: 0.6,
    minPnL: -800,
    maxPnL: 1200,
    description: "Starts negative, trends positive over time",
  },
  cautious_day: {
    upBias: 0.55,
    minPnL: 50,
    maxPnL: 200,
    description: "Conservative positive day (+$50 to +$200)",
  },
};

// =============================================================================
// Simulation Types
// =============================================================================

export type SimulationType =
  | "cash_exit" // Demonstrate cash exit threshold
  | "profit_lock" // Demonstrate profit protection
  | "copy_trade"; // Demonstrate copy trading between accounts

export type SimulationEventType =
  | "started"
  | "threshold_hit"
  | "position_copied"
  | "complete"
  | "stopped";

export interface SimulationEvent {
  type: SimulationEventType;
  message?: string;
  data?: Record<string, number | string>;
}

export interface SimulationState {
  isRunning: boolean;
  type: SimulationType | null;
  startTime: Date | null;
  config: SimulationConfig | null;
  progress: number;
  lastEvent?: SimulationEvent | null;
}

export interface CashExitSimConfig {
  type: "cash_exit";
  threshold: number; // P&L threshold to trigger (e.g., -500)
  driftDuration: number; // How long to drift toward threshold (ms)
  groupId?: number; // Which group to apply to
}

export interface ProfitLockSimConfig {
  type: "profit_lock";
  lockAtProfit: number; // Lock at this profit amount
  lockPercentage: number; // Lock this percentage
  driftDuration: number;
}

export interface CopyTradeSimConfig {
  type: "copy_trade";
  leaderAccountId: number;
  followerAccountIds: number[];
  copyDelay: number; // Delay before followers copy (ms)
  symbol?: string; // Symbol for leader's trade (e.g., "ESZ4")
  side?: "long" | "short"; // Side for leader's trade
}

export type SimulationConfig =
  | CashExitSimConfig
  | ProfitLockSimConfig
  | CopyTradeSimConfig;

// =============================================================================
// Mock Account Types (Prop Firm Style)
// =============================================================================

export type PropFirmStyle = "tpt" | "apex" | "topstep";

export interface MockAccountConfig {
  id: number;
  name: string;
  propFirmStyle: PropFirmStyle;
  balance: number;
  groupIds: number[];
  isLeader: boolean; // For copy trading demos
}

// =============================================================================
// Core Config & Context Types
// =============================================================================

export interface MockDataConfig {
  /** Whether mock data is enabled (only in development) */
  enabled: boolean;
  /** Simulate live PnL updates */
  simulateLivePnL: boolean;
  /** Update interval for live PnL simulation (ms) */
  pnlUpdateInterval: number;
  /** Simulate WebSocket events */
  simulateWebSocket: boolean;
  /** Number of mock positions to generate */
  positionCount: number;
  /** Number of mock accounts to generate */
  accountCount: number;
  /** Current PnL preset */
  pnlPreset: PnLPreset;
  /** Update speed */
  updateSpeed: UpdateSpeed;
  /** Active symbols to show */
  activeSymbols: string[];
  /** Active group IDs */
  activeGroups: number[];
}

export interface LivePnLState {
  positionId: number;
  currentPnL: number;
  percentChange: number;
  lastUpdate: Date;
  trend: "up" | "down" | "neutral";
}

export interface MockDataContextValue {
  config: MockDataConfig;
  isEnabled: boolean;
  /** Whether control panel is open */
  isPanelOpen: boolean;
  /** Open/close control panel */
  setPanelOpen: (open: boolean) => void;
  /** Live PnL values keyed by position ID */
  livePnL: Map<number, LivePnLState>;
  /** Current simulation state */
  simulation: SimulationState;
  /** Start live PnL simulation */
  startLivePnL: () => void;
  /** Stop live PnL simulation */
  stopLivePnL: () => void;
  /** Reset all mock data to initial state */
  resetMockData: () => void;
  /** Trigger a mock trade execution */
  triggerMockTrade: (positionId: number, action: "open" | "close") => void;
  /** Update config */
  updateConfig: (updates: Partial<MockDataConfig>) => void;
  /** Set PnL preset */
  setPnLPreset: (preset: PnLPreset) => void;
  /** Set update speed */
  setUpdateSpeed: (speed: UpdateSpeed) => void;
  /** Add positions */
  addPositions: (count: number) => void;
  /** Clear all positions */
  clearAllPositions: () => void;
  /** Start a simulation demo (optional startDelay in ms for countdown) */
  startSimulation: (config: SimulationConfig, startDelay?: number) => void;
  /** Stop current simulation */
  stopSimulation: () => void;
  /** Countdown seconds remaining before demo starts (0 = no countdown) */
  countdown: number;

  // =========================================================================
  // Demo Studio Methods (Phase 2 Enhancement)
  // =========================================================================

  /** Current studio configuration */
  studioConfig: StudioConfig;
  /** Current demo script state */
  demoScriptState: DemoScriptState;
  /** Apply a scenario preset (Win Streak, Drawdown Recovery, etc.) */
  applyScenarioPreset: (presetId: DemoScenarioPresetId) => void;
  /** Start running a demo script */
  startDemoScript: (script: DemoScript) => void;
  /** Pause the current demo script */
  pauseDemoScript: () => void;
  /** Resume the paused demo script */
  resumeDemoScript: () => void;
  /** Stop and reset the demo script */
  stopDemoScript: () => void;
  /** Jump to a specific step in the script */
  jumpToScriptStep: (index: number) => void;
  /** Manually advance to the next script step */
  advanceScriptStep: () => void;
  /** Enable/disable presenter mode */
  setPresenterMode: (enabled: boolean) => void;
  /** Update CTA overlay configuration */
  setCtaOverlay: (updates: Partial<CTAOverlayConfig>) => void;
}

export const DEFAULT_MOCK_CONFIG: MockDataConfig = {
  enabled: process.env.NODE_ENV === "development",
  simulateLivePnL: true,
  pnlUpdateInterval: 1500, // 1.5 seconds (matches positions refetch interval)
  simulateWebSocket: true,
  positionCount: 5,
  accountCount: 7,
  pnlPreset: "realistic",
  updateSpeed: "normal",
  activeSymbols: ["ESZ4", "NQZ4", "MESZ4", "CLZ4", "GCZ4"],
  activeGroups: [1, 2],
};

// =============================================================================
// Available Symbols
// =============================================================================

export const AVAILABLE_SYMBOLS = [
  { symbol: "ESZ4", name: "E-mini S&P 500", category: "Index" },
  { symbol: "NQZ4", name: "E-mini Nasdaq-100", category: "Index" },
  { symbol: "MESZ4", name: "Micro E-mini S&P 500", category: "Index" },
  { symbol: "MNQZ4", name: "Micro E-mini Nasdaq", category: "Index" },
  { symbol: "CLZ4", name: "Crude Oil", category: "Energy" },
  { symbol: "GCZ4", name: "Gold", category: "Metals" },
  { symbol: "6EZ4", name: "Euro FX", category: "Currency" },
  { symbol: "ZBZ4", name: "30-Year T-Bond", category: "Bonds" },
];

// =============================================================================
// Demo Studio Types (Phase 2 Enhancement)
// =============================================================================

export type DemoScenarioPresetId =
  | "win_streak"
  | "drawdown_recovery"
  | "risk_halt"
  | "copy_trade_surge";

export interface DemoScenarioPreset {
  id: DemoScenarioPresetId;
  title: string;
  description: string;
  duration: number; // seconds
  pnlPreset: PnLPreset;
  simulationConfig?: SimulationConfig;
}

export type DemoStepAction =
  | "position_add"
  | "pnl_shift"
  | "simulation_start"
  | "cta_show"
  | "cta_hide"
  | "wait";

export interface DemoStep {
  id: string;
  label: string;
  action: DemoStepAction;
  durationMs?: number;
  waitForEvent?: SimulationEventType;
  config?: Record<string, unknown>;
}

export interface DemoScript {
  id: string;
  title: string;
  description?: string;
  steps: DemoStep[];
}

export interface DemoScriptState {
  activeScriptId: string | null;
  activeStepIndex: number;
  isRunning: boolean;
  isPaused: boolean;
}

export type CTAOverlayPosition =
  | "bottom-left"
  | "bottom-right"
  | "top-left"
  | "top-right";

export interface CTAOverlayConfig {
  enabled: boolean;
  text: string;
  subtext?: string;
  position: CTAOverlayPosition;
}

export interface StudioConfig {
  presenterMode: boolean;
  ctaOverlay: CTAOverlayConfig;
}

export const DEFAULT_STUDIO_CONFIG: StudioConfig = {
  presenterMode: false,
  ctaOverlay: {
    enabled: false,
    text: "Join SigmaVue",
    subtext: "Autonomous AI Trading",
    position: "bottom-right",
  },
};

export const DEFAULT_DEMO_SCRIPT_STATE: DemoScriptState = {
  activeScriptId: null,
  activeStepIndex: 0,
  isRunning: false,
  isPaused: false,
};

// =============================================================================
// Simulation Hub Types (Centralized Store)
// =============================================================================

/** Shared union for all close-reason values. Used by closePosition, closeAllPositions, and SimTrade. */
export type ClosedByReason = "user" | "agent" | "stop_loss" | "take_profit" | "emergency";

export interface SimPosition {
  id: string;
  symbol: string;
  side: "long" | "short";
  quantity: number;
  entryPrice: number;
  currentPrice: number;
  unrealizedPnl: number;
  status: "active" | "pending";
  broker: string;
  accountId: number;
  /** Display name resolved from accountId (avoids repeated lookups) */
  accountName: string;
  tickSize: number;
  pointValue: number;
  /** Strategy that opened this position (null = manual/user) */
  strategy: string | null;
  openedAt: string;
  openedBy: "user" | "agent" | "copy_trade";
}

export interface SimTrade {
  id: string;
  positionId: string;
  symbol: string;
  side: "long" | "short";
  quantity: number;
  entryPrice: number;
  exitPrice: number;
  realizedPnl: number;
  tickSize: number;
  /** Account ID that held this position (for attribution in closed-trade history) */
  accountId: number;
  /** Account display name (avoids reverse lookups in adapters) */
  accountName: string;
  openedAt: string;
  closedAt: string;
  closedBy: ClosedByReason;
  duration: number;
}

export interface SimAccount {
  id: number;
  name: string;
  broker: string;
  balance: string;
  equity: string;
  status: "active" | "inactive";
  environment?: "demo" | "live";
  realizedPnl: number;
}

export interface SimGroup {
  id: number;
  name: string;
  accounts: string[];
  copyTradingEnabled: boolean;
  leaderAccount: string | null;
  cashExitThreshold: number;
  profitLockEnabled: boolean;
  profitLockAmount: number;
  profitLockPercentage: number;
  riskMultiplier: number;
  /** Whether the group is currently active (used by simulations) */
  isActive: boolean;
}

export interface SimOrder {
  id: number;
  broker: string;
  account: string;
  instrument: string;
  direction: "long" | "short";
  orderType: "market" | "limit" | "stop";
  requestedVolume: number;
  filledVolume: number;
  status: "pending" | "filled" | "cancelled" | "partially_filled";
  createdAt: string;
}

export interface AgentEvent {
  id: string;
  agentName: string;
  action: string;
  message: string;
  timestamp: string;
  severity: "info" | "warning" | "critical";
  relatedPositionId?: string;
}

export interface SimChatMessage {
  id: string;
  role: "assistant" | "system";
  content: string;
  timestamp: string;
  relatedEvent?: string;
}
